/**
 * Roadmap Data Loader
 * 
 * Handles loading of roadmap curriculum data including macro and meso levels
 * with proper ordering and active status filtering.
 */

import { supabase } from '@/integrations/supabase/client';
import { indexedDBService } from '../../indexeddb';
import {
  RoadmapMacroTransformer,
  RoadmapMesoTransformer
} from '../DataTransformer';
import { BaseLoader, type ExtendedLoaderResult } from './BaseLoader';

/**
 * Roadmap Data Loader Implementation
 * 
 * Specializes in loading roadmap curriculum data with proper ordering
 * and filtering for active curriculum items only.
 */
export class RoadmapDataLoader extends BaseLoader {
  /**
   * Load all roadmap data (macro and meso curriculum)
   */
  async load(_userId: string): Promise<ExtendedLoaderResult> {
    const errors: string[] = [];
    let macroCount = 0;
    let mesoCount = 0;

    try {
      // Load macro curriculum
      const macroResult = await this.loadMacroCurriculum();
      macroCount = macroResult.count;
      errors.push(...macroResult.errors);

      // Load meso curriculum
      const mesoResult = await this.loadMesoCurriculum();
      mesoCount = mesoResult.count;
      errors.push(...mesoResult.errors);

      return this.createExtendedLoaderResult({
        roadmapMacro: macroCount,
        roadmapMeso: mesoCount
      }, errors);
    } catch (error) {
      const errorMessage = this.handleError(error, 'Roadmap data loading failed');
      return this.createExtendedLoaderResult({
        roadmapMacro: 0,
        roadmapMeso: 0
      }, [errorMessage]);
    }
  }

  /**
   * Load macro curriculum data
   */
  async loadMacroCurriculum(): Promise<{ count: number; errors: string[] }> {
    try {
      const { data, error } = await supabase
        .from('roadmap_macro')
        .select('*')
        .eq('is_active', true)
        .order('order_index');

      if (error) {
        return { count: 0, errors: [`Macro curriculum: ${error.message}`] };
      }

      if (data && data.length > 0) {
        const offlineMacro = data.map(RoadmapMacroTransformer.fromSupabase);
        await indexedDBService.offlineDB.putMany('roadmap_macro', offlineMacro);
        return { count: data.length, errors: [] };
      }

      return { count: 0, errors: [] };
    } catch (error) {
      const errorMessage = this.handleError(error, 'Macro curriculum loading failed');
      return { count: 0, errors: [errorMessage] };
    }
  }

  /**
   * Load meso curriculum data
   */
  async loadMesoCurriculum(): Promise<{ count: number; errors: string[] }> {
    try {
      const { data, error } = await supabase
        .from('roadmap_meso')
        .select('*')
        .eq('is_active', true)
        .order('order_index');

      if (error) {
        return { count: 0, errors: [`Meso curriculum: ${error.message}`] };
      }

      if (data && data.length > 0) {
        const offlineMeso = data.map(RoadmapMesoTransformer.fromSupabase);
        await indexedDBService.offlineDB.putMany('roadmap_meso', offlineMeso);
        return { count: data.length, errors: [] };
      }

      return { count: 0, errors: [] };
    } catch (error) {
      const errorMessage = this.handleError(error, 'Meso curriculum loading failed');
      return { count: 0, errors: [errorMessage] };
    }
  }

  /**
   * Load roadmap data for specific belt levels
   */
  async loadByBeltLevel(beltLevel: string): Promise<ExtendedLoaderResult> {
    const errors: string[] = [];
    let macroCount = 0;
    let mesoCount = 0;

    try {
      // Load macro curriculum for belt level
      const { data: macroData, error: macroError } = await supabase
        .from('roadmap_macro')
        .select('*')
        .eq('is_active', true)
        .eq('belt_level', beltLevel)
        .order('order_index');

      if (macroError) {
        errors.push(`Macro curriculum for ${beltLevel}: ${macroError.message}`);
      } else if (macroData && macroData.length > 0) {
        const offlineMacro = macroData.map(RoadmapMacroTransformer.fromSupabase);
        await indexedDBService.offlineDB.putMany('roadmap_macro', offlineMacro);
        macroCount = macroData.length;
      }

      // Load meso curriculum for belt level
      const { data: mesoData, error: mesoError } = await supabase
        .from('roadmap_meso')
        .select('*')
        .eq('is_active', true)
        .eq('belt_level', beltLevel)
        .order('order_index');

      if (mesoError) {
        errors.push(`Meso curriculum for ${beltLevel}: ${mesoError.message}`);
      } else if (mesoData && mesoData.length > 0) {
        const offlineMeso = mesoData.map(RoadmapMesoTransformer.fromSupabase);
        await indexedDBService.offlineDB.putMany('roadmap_meso', offlineMeso);
        mesoCount = mesoData.length;
      }

      return this.createExtendedLoaderResult({
        roadmapMacro: macroCount,
        roadmapMeso: mesoCount
      }, errors);
    } catch (error) {
      const errorMessage = this.handleError(error, `Roadmap data loading for ${beltLevel} failed`);
      return this.createExtendedLoaderResult({
        roadmapMacro: 0,
        roadmapMeso: 0
      }, [errorMessage]);
    }
  }

  /**
   * Load all belt levels if configuration allows
   */
  async loadAllBeltLevels(): Promise<ExtendedLoaderResult> {
    if (!this.config.includeAllBeltLevels) {
      return this.createExtendedLoaderResult({
        roadmapMacro: 0,
        roadmapMeso: 0
      });
    }

    return this.load(''); // Belt level filtering happens in the database query
  }

  /**
   * Load roadmap data by order range
   */
  async loadByOrderRange(startOrder: number, endOrder: number): Promise<ExtendedLoaderResult> {
    const errors: string[] = [];
    let macroCount = 0;
    let mesoCount = 0;

    try {
      // Load macro curriculum in order range
      const { data: macroData, error: macroError } = await supabase
        .from('roadmap_macro')
        .select('*')
        .eq('is_active', true)
        .gte('order_index', startOrder)
        .lte('order_index', endOrder)
        .order('order_index');

      if (macroError) {
        errors.push(`Macro curriculum order range: ${macroError.message}`);
      } else if (macroData && macroData.length > 0) {
        const offlineMacro = macroData.map(RoadmapMacroTransformer.fromSupabase);
        await indexedDBService.offlineDB.putMany('roadmap_macro', offlineMacro);
        macroCount = macroData.length;
      }

      // Load meso curriculum in order range
      const { data: mesoData, error: mesoError } = await supabase
        .from('roadmap_meso')
        .select('*')
        .eq('is_active', true)
        .gte('order_index', startOrder)
        .lte('order_index', endOrder)
        .order('order_index');

      if (mesoError) {
        errors.push(`Meso curriculum order range: ${mesoError.message}`);
      } else if (mesoData && mesoData.length > 0) {
        const offlineMeso = mesoData.map(RoadmapMesoTransformer.fromSupabase);
        await indexedDBService.offlineDB.putMany('roadmap_meso', offlineMeso);
        mesoCount = mesoData.length;
      }

      return this.createExtendedLoaderResult({
        roadmapMacro: macroCount,
        roadmapMeso: mesoCount
      }, errors);
    } catch (error) {
      const errorMessage = this.handleError(error, 'Roadmap order range loading failed');
      return this.createExtendedLoaderResult({
        roadmapMacro: 0,
        roadmapMeso: 0
      }, [errorMessage]);
    }
  }

  /**
   * Get roadmap statistics
   */
  async getRoadmapStats(): Promise<{
    totalMacroItems: number;
    totalMesoItems: number;
    activeMacroItems: number;
    activeMesoItems: number;
    beltLevels: string[];
  }> {
    try {
      const macroItems = await indexedDBService.roadmapMacro.getActive();
      const mesoItems = await indexedDBService.roadmapMeso.getActive();
      
      const totalMacroItems = macroItems.length;
      const totalMesoItems = mesoItems.length;
      const activeMacroItems = macroItems.filter(item => item.is_active === 1).length;
      const activeMesoItems = mesoItems.filter(item => item.is_active === 1).length;
      
      // Get unique belt colors
      const beltLevels = Array.from(new Set([
        ...macroItems.map(item => item.belt_color),
        ...mesoItems.map(item => item.belt_color)
      ])).filter(Boolean).sort();

      return {
        totalMacroItems,
        totalMesoItems,
        activeMacroItems,
        activeMesoItems,
        beltLevels
      };
    } catch (error) {
      console.warn('Failed to calculate roadmap stats:', error);
      return {
        totalMacroItems: 0,
        totalMesoItems: 0,
        activeMacroItems: 0,
        activeMesoItems: 0,
        beltLevels: []
      };
    }
  }

  /**
   * Get curriculum progress for a specific belt color
   */
  async getBeltLevelProgress(beltColor: string): Promise<{
    macroItems: number;
    mesoItems: number;
    totalItems: number;
  }> {
    try {
      const macroItems = await indexedDBService.roadmapMacro.getByBeltColor(beltColor);
      const mesoItems = await indexedDBService.roadmapMeso.getByBeltColor(beltColor);
      
      return {
        macroItems: macroItems.length,
        mesoItems: mesoItems.length,
        totalItems: macroItems.length + mesoItems.length
      };
    } catch (error) {
      console.warn(`Failed to get progress for belt color ${beltColor}:`, error);
      return {
        macroItems: 0,
        mesoItems: 0,
        totalItems: 0
      };
    }
  }
}

// Export singleton instance
export const roadmapDataLoader = new RoadmapDataLoader();
